/**
 * Module 1.12 — the KMD (käibedeklaratsioon / VAT return) forecast.
 *
 * This is the ONE definition of how the mirrored document stream becomes dated
 * KMD obligations in the cash-flow calendar (master rule: one truth per number,
 * L9 — integer cents only). It CONSUMES the 1.2 engine's `summarizeKmd` (never
 * re-adds VAT itself) and the 1.1 calendar's statutory working-day shift:
 *
 *  - accrual month M = the month of each document's `documentDate` (the same
 *    date the accrual-basis lens uses) — sales (kind `income`) contribute
 *    OUTPUT VAT, purchases (kind `cost`) contribute INPUT VAT, both SIGNED as
 *    stored, so credit notes (stored negative) net out by plain addition;
 *  - the obligation is due on the 20th of M+1 (KMS § 27), a statutory deadline
 *    that shifts FORWARD to the first working day — the same rule as TSD;
 *  - only a POSITIVE net position (`output − input > 0`) is a dated cash-out
 *    obligation; a refund month produces no row (money coming back is not a
 *    payment to schedule);
 *  - certainty follows §6.3: while the accrual month is still running the
 *    figure can only grow → `forecast`; once the month has ended the document
 *    set is final → `committed`.
 *
 * Pure — no I/O, no `Date.now()` (the caller passes `todayIso`), golden-tested.
 * WHICH rows feed it (mirror sources only, prepayment expectations excluded)
 * is the API materializer's boundary; this module aggregates what it is given.
 *
 * ## KMD settlement placements (added in task #169)
 *
 * A `tax:kmd` entry whose `kmdSettlement` is populated is resolved by
 * `resolveKmdPlacements` instead of the generic `resolvePlacements`. The rules:
 *
 *  - every EFFECTIVE installment (not superseded) becomes its OWN `actual`,
 *    exempt-money placement on its own `paidOn` date;
 *  - mixed manual + LHV installments ADD, never winner-take-all;
 *  - `remainingForecastCents = finalized ? 0 : max(forecastGross − effectiveActualCents, 0)`;
 *    above-forecast actual is never clipped;
 *  - remaining forecast is ONE forecast placement on the original `expectedDate`
 *    (pinned, never generically carried to today) — finalized removes it even
 *    when actual < forecast;
 *  - under `accrualBasis` a single placement of the immutable baseline sits at
 *    the entry's `expectedDate` (the due date), the same rule as every other
 *    accrual entry, ignoring settlement state.
 */

import type { Money } from './money';
import {
  accrualRelevant,
  kmdEffectiveSettlement,
  resolveEntry,
  resolveKmdPlacements,
  type CashFlowEntry,
  type CashFlowKind,
  type CashFlowSource,
  type FilteredSumOptions,
  type KmdPaymentEvent,
  type KmdSettlement,
  type MoneySum,
} from './cashflow';
import { summarizeKmd, type KmdSummary } from './vatEngine';
import { firstWorkingDay, taxKindOf, EE_HOLIDAYS_2026, type Holiday } from './calendar';
import { addMonthsClamped } from './loan';

/**
 * The reconcile scope owning every materialized KMD row — the external-id
 * namespace is `tax:kmd:<accrual month>`. Defined HERE (the KMD domain home)
 * so the API materializer that writes the ids and the web helpers that parse
 * them share the ONE literal (master rule: one truth).
 */
export const KMD_EXTERNAL_ID_PREFIX = 'tax:kmd:';

/**
 * Mirror sources whose documents carry the VAT position — real external
 * documents with real VAT lines. The ONE list both the API materializer's
 * read filter and the web's declaration drill apply, so the drill lists
 * exactly the rows the figure summed (master rule; L14).
 */
export const KMD_MIRROR_SOURCES = ['smartaccounts', 'erply', 'pipedrive'] as const satisfies readonly CashFlowSource[];

/** One mirrored document row feeding the KMD position. */
export interface KmdSourceRow {
  /** `income` = a sale (output VAT); `cost` = a purchase (input VAT). */
  kind: CashFlowKind;
  /** The document's canonical money, SIGNED as stored (credit notes negative). */
  money: Money;
  /** ISO day the document was issued — the VAT accrual date, `YYYY-MM-DD`. */
  documentDate: string;
}

/** One dated KMD obligation the calendar should carry. */
export interface KmdObligation {
  /** Accrual month `YYYY-MM` the declaration covers. */
  accrualMonth: string;
  /** Payment/declaration deadline — see {@link kmdDueDate}. */
  dueDate: string;
  /** `forecast` while the accrual month is running, `committed` after it ends. */
  certainty: 'committed' | 'forecast';
  /** The full 1.2 position (output/input, per-rate boxes, net payable). */
  summary: KmdSummary;
}

const ISO_MONTH = /^\d{4}-\d{2}$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The ONE KMD deadline definition: the declaration for accrual month M is due
 * on the 20th of M+1 (KMS § 27) — a statutory deadline, so it shifts FORWARD
 * to the first working day (like TSD, unlike wages).
 */
export function kmdDueDate(
  month: string,
  holidays: readonly Holiday[] = EE_HOLIDAYS_2026,
): string {
  if (!ISO_MONTH.test(month)) {
    throw new RangeError(`kmdDueDate: expected an ISO month (YYYY-MM), received ${month}`);
  }
  return firstWorkingDay(`${addMonthsClamped(`${month}-01`, 1).slice(0, 8)}20`, holidays);
}

/**
 * Fold mirrored document rows into dated KMD obligations, one per accrual
 * month, keeping only months whose deadline is `todayIso` or later (an already
 * past deadline is history — the real payment lives in the mirror, and a
 * generated duplicate would double-count it). Months are emitted in calendar
 * order. All arithmetic is `summarizeKmd`'s integer-cent addition (L9).
 */
export function kmdObligations(input: {
  rows: readonly KmdSourceRow[];
  /** Tallinn calendar date, `YYYY-MM-DD`. */
  todayIso: string;
  holidays?: readonly Holiday[];
}): KmdObligation[] {
  const { rows, todayIso, holidays = EE_HOLIDAYS_2026 } = input;
  if (!ISO_DATE.test(todayIso)) {
    throw new RangeError(`kmdObligations: expected an ISO todayIso (YYYY-MM-DD), received ${todayIso}`);
  }
  const todayMonth = todayIso.slice(0, 7);

  const byMonth = new Map<string, KmdSourceRow[]>();
  for (const row of rows) {
    if (!ISO_DATE.test(row.documentDate)) {
      throw new RangeError(
        `kmdObligations: expected an ISO documentDate (YYYY-MM-DD), received ${row.documentDate}`,
      );
    }
    const month = row.documentDate.slice(0, 7);
    const bucket = byMonth.get(month);
    if (bucket) {
      bucket.push(row);
    } else {
      byMonth.set(month, [row]);
    }
  }

  const out: KmdObligation[] = [];
  for (const month of [...byMonth.keys()].sort()) {
    const bucket = byMonth.get(month)!;
    const summary = summarizeKmd(
      bucket.map((row) => ({
        money: row.money,
        direction: row.kind === 'income' ? ('output' as const) : ('input' as const),
      })),
    );
    if (summary.netPayable <= 0) {
      continue;
    }
    const dueDate = kmdDueDate(month, holidays);
    if (dueDate < todayIso) {
      continue;
    }
    out.push({
      accrualMonth: month,
      dueDate,
      certainty: month >= todayMonth ? 'forecast' : 'committed',
      summary,
    });
  }
  return out;
}

const KMD_EXTERNAL_ID = /^tax:kmd:(\d{4}-\d{2})$/;

/**
 * Parse the accrual (declaration) month out of a materialized KMD entry's
 * external id — `tax:kmd:2026-07` → `2026-07`, anything else → null. The ONE
 * inverse of the id the materializer writes (same precedent as the loan /
 * payment-plan installment parsers), so the web's declaration drill never
 * string-slices ids itself.
 */
export function kmdDeclarationMonthOf(externalId: string | null): string | null {
  if (externalId === null) return null;
  const match = KMD_EXTERNAL_ID.exec(externalId);
  return match === null ? null : match[1];
}

/**
 * The "KMD — käibemaks" informational footer row — the materialized KMD
 * declaration entries (`taxKindOf` = KMD: the `tax` source on the
 * `tax_agreement` line) summed over the window with the same window/certainty
 * rules as `payrollTax` / `paymentPlanFlow`, so a month column shows exactly
 * the declaration payment(s) FALLING DUE in it (the previous month's
 * documents), never a re-added VAT position. The figure is the SAME
 * materialized entry the Maksud line carries — an INFO row, never summed into
 * a total (double-count). Integer cents (L9); pure.
 *
 * Entries with `kmdSettlement` attached are resolved via
 * {@link resolveKmdPlacements} so the info row and the line totals share the
 * same slices — they can never drift.
 */
export function kmdDueFlow(entries: CashFlowEntry[], options: FilteredSumOptions): MoneySum {
  const { today } = options;
  if (!ISO_DATE.test(today)) {
    throw new RangeError(`kmdDueFlow: expected an ISO today (YYYY-MM-DD), received ${today}`);
  }
  if (options.from !== undefined && !ISO_DATE.test(options.from)) {
    throw new RangeError(`kmdDueFlow: expected an ISO from (YYYY-MM-DD), received ${options.from}`);
  }
  if (options.to !== undefined && !ISO_DATE.test(options.to)) {
    throw new RangeError(`kmdDueFlow: expected an ISO to (YYYY-MM-DD), received ${options.to}`);
  }

  const basis = options.basis ?? 'cashBasis';
  const sum: MoneySum = { net: 0, vat: 0, gross: 0 };

  for (const e of entries) {
    if (taxKindOf(e) !== 'KMD') continue;

    // Entries with kmdSettlement resolve through the canonical KMD placement logic
    // (both bases) so the info row and the primary rollup / register / export share
    // the exact same slices — they can never drift (master rule; L14).
    if (e.kmdSettlement) {
      const placements = resolveKmdPlacements(
        e as CashFlowEntry & { kmdSettlement: KmdSettlement },
        today,
        basis,
      );
      for (const p of placements) {
        if (options.from !== undefined && p.date < options.from) continue;
        if (options.to !== undefined && p.date > options.to) continue;
        if (options.certainty !== undefined && p.certainty !== options.certainty) continue;
        sum.net += p.money.net;
        sum.vat += p.money.vat;
        sum.gross += p.money.gross;
      }
      continue;
    }

    // KMD entries WITHOUT settlement metadata keep the existing generic behavior.
    const r = resolveEntry(e, today, basis);
    if (options.from !== undefined && r.effectiveDate < options.from) continue;
    if (options.to !== undefined && r.effectiveDate > options.to) continue;
    if (options.certainty !== undefined && r.effectiveCertainty !== options.certainty) continue;
    sum.net += e.money.net;
    sum.vat += e.money.vat;
    sum.gross += e.money.gross;
  }

  return sum;
}

// --------------------------------------------------------------- backcast

/**
 * One mirrored document row for the PAST-month backcast — {@link KmdSourceRow}
 * plus the two boundary fields the backcast must filter on itself (the web
 * feeds it the raw shared entry stream, not a pre-filtered API read): the
 * entry's source and its document type.
 */
export interface KmdBackcastRow extends KmdSourceRow {
  source: CashFlowSource;
  /** `doc_type` of the mirror row — prepayment expectations carry no VAT position. */
  docType: string | null;
}

/**
 * Recompute a PAST declaration month's KMD position from the mirrored
 * document stream — the backcast twin of the materializer's forward pass,
 * applying the IDENTICAL row filter (`KMD_MIRROR_SOURCES` + `accrualRelevant`
 * + nonzero VAT + `documentDate` in the accrual month — the same predicate
 * the declaration drill lists), then the SAME `summarizeKmd` fold. One truth:
 * the tooltip's "prognoos" is exactly what the calendar WOULD have carried had
 * the month still been ahead. Integer cents (L9); pure.
 */
export function kmdBackcastSummary(
  rows: readonly KmdBackcastRow[],
  month: string,
): KmdSummary {
  if (!ISO_MONTH.test(month)) {
    throw new RangeError(`kmdBackcastSummary: expected an ISO month (YYYY-MM), received ${month}`);
  }
  return summarizeKmd(
    rows
      .filter(
        (row) =>
          (KMD_MIRROR_SOURCES as readonly CashFlowSource[]).includes(row.source) &&
          accrualRelevant(row) &&
          row.money.vat !== 0 &&
          row.documentDate.slice(0, 7) === month,
      )
      .map((row) => ({
        money: row.money,
        direction: row.kind === 'income' ? ('output' as const) : ('input' as const),
      })),
  );
}

/**
 * The PAST declaration months whose deadline falls inside a column window —
 * the backcast twin of the window rule `kmdDueFlow` applies to materialized
 * entries: accrual month M belongs to the column containing its (working-day
 * shifted) due date. "Past" = the deadline is strictly before `todayIso`
 * (a deadline today or later is a materialized entry — counting it here too
 * would double-show it). `earliestMonth` guards the data horizon: accrual
 * months before the mirror's first synced month (e.g. 2025-12 → the January
 * column) have NO documents to back a figure — emitting a fake €0 would read
 * as "no VAT was due", so they are excluded outright.
 */
export function kmdPastDeclarationMonths(input: {
  from: string;
  to: string;
  /** Tallinn calendar date, `YYYY-MM-DD`. */
  todayIso: string;
  /** First accrual month (`YYYY-MM`) the mirror actually covers. */
  earliestMonth: string;
  holidays?: readonly Holiday[];
}): string[] {
  const { from, to, todayIso, earliestMonth, holidays = EE_HOLIDAYS_2026 } = input;
  for (const [name, value] of [['from', from], ['to', to], ['todayIso', todayIso]] as const) {
    if (!ISO_DATE.test(value)) {
      throw new RangeError(`kmdPastDeclarationMonths: expected an ISO ${name} (YYYY-MM-DD), received ${value}`);
    }
  }
  if (!ISO_MONTH.test(earliestMonth)) {
    throw new RangeError(`kmdPastDeclarationMonths: expected an ISO earliestMonth (YYYY-MM), received ${earliestMonth}`);
  }
  const out: string[] = [];
  // Candidate accrual months: due 20th of M+1 → M ranges from two months
  // before the window start (shift can only move a deadline FORWARD) to the
  // window end's own month.
  // (Manual backward step — addMonthsClamped only walks forward.)
  const [fy, fm] = from.slice(0, 7).split('-').map(Number);
  const startTotal = fy * 12 + (fm - 1) - 2;
  let cursor = `${Math.floor(startTotal / 12)}-${String((startTotal % 12) + 1).padStart(2, '0')}`;
  const last = to.slice(0, 7);
  while (cursor <= last) {
    const due = kmdDueDate(cursor, holidays);
    if (cursor >= earliestMonth && due >= from && due <= to && due < todayIso) {
      out.push(cursor);
    }
    cursor = addMonthsClamped(`${cursor}-01`, 1).slice(0, 7);
  }
  return out;
}

/**
 * Forecast-vs-actual variance of one declaration month — the ONE definition
 * the tooltip renders (master rule: no bare monetary arithmetic in
 * components). `diffCents` = actual − forecast (positive = the real
 * declaration was LARGER than the documents implied). `diffPct` is the same
 * difference as a percentage of the forecast (1 decimal, rounded half away
 * from zero), or null when the forecast is 0 (a ratio against nothing).
 */
export function kmdVariance(
  forecastCents: number,
  actualCents: number,
): { diffCents: number; diffPct: number | null } {
  if (!Number.isInteger(forecastCents) || !Number.isInteger(actualCents)) {
    throw new RangeError('kmdVariance: expected integer cents');
  }
  const diffCents = actualCents - forecastCents;
  if (forecastCents === 0) return { diffCents, diffPct: null };
  const raw = (diffCents / Math.abs(forecastCents)) * 1000;
  const diffPct = (raw < 0 ? -Math.round(-raw) : Math.round(raw)) / 10;
  return { diffCents, diffPct };
}

// --------------------------------------------------------------- KMD settlement

/**
 * Summary of a KMD settlement computation — the ONE place the settlement
 * figures live (master rule: no component does its own KMD money arithmetic).
 *
 * `forecastCents`         — the immutable forecast baseline (entry.money.gross).
 * `actualCents`           — sum of all EFFECTIVE (non-superseded) installments.
 * `remainingForecastCents`— max(forecastCents − actualCents, 0), or 0 when
 *                           finalized (even if actual is below forecast).
 *                           Above-forecast actual is never clipped.
 * `status`                — `lhv` when any effective payment came from LHV,
 *                           `manual` when any came from a human, else `forecast`.
 * `kmdVariance`           — {@link kmdVariance} of forecastCents vs actualCents
 *                           (the diff the declaration tooltip renders).
 */
export interface KmdSettlementSummary {
  /** Immutable forecast baseline, integer cents. */
  forecastCents: number;
  /** Sum of effective (non-superseded) installments, integer cents. */
  actualCents: number;
  /**
   * Remaining forecast owed: `finalized ? 0 : max(forecastCents − actualCents, 0)`.
   * Above-forecast actual is never clipped.
   */
  remainingForecastCents: number;
  /** Highest-priority payment source seen: `lhv` > `manual` > `forecast`. */
  status: 'forecast' | 'manual' | 'lhv';
  /** Variance of forecast vs actual via {@link kmdVariance}. */
  kmdVariance: { diffCents: number; diffPct: number | null };
}

/**
 * Derive a {@link KmdSettlementSummary} from a {@link KmdSettlement} and the
 * immutable forecast baseline. The effective cents (validation of the ISO month,
 * per-payment positive integer `amountCents` + ISO `paidOn`, the superseded
 * exclusion, and the `finalized ? 0 : max(forecast − actual, 0)` remainder) are
 * computed by cashflow's canonical {@link kmdEffectiveSettlement} — the SAME math
 * the placement resolver uses, so the tooltip's figures and the projection slices
 * can never drift (master rule; one truth; L14). This layer only adds the DISPLAY
 * fields on top: the `status` cue and the {@link kmdVariance}. Pure; L9.
 */
export function summarizeKmdSettlement(
  settlement: KmdSettlement,
  forecastCents: number,
): KmdSettlementSummary {
  const { actualCents, remainingForecastCents, hasLhv, hasManual } = kmdEffectiveSettlement(
    settlement,
    forecastCents,
  );

  const status: KmdSettlementSummary['status'] = hasLhv
    ? 'lhv'
    : hasManual
      ? 'manual'
      : 'forecast';

  return {
    forecastCents,
    actualCents,
    remainingForecastCents,
    status,
    kmdVariance: kmdVariance(forecastCents, actualCents),
  };
}

// The canonical KMD placement resolver lives in cashflow.ts (so resolvePlacements
// / resolveEntry can delegate without a circular import); re-export it here so KMD
// consumers still reach it through the KMD domain module (one import surface).
export { resolveKmdPlacements };

// Re-export KmdSettlement types so consumers get them from kmdForecast.
export type { KmdPaymentEvent, KmdSettlement };
